import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import { requestLinkSchema } from "@passa-a-pagina/shared";
import { api, ApiError } from "../lib/api";
import "./auth.css";

export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [ageConsent, setAgeConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; ageConsent?: string; form?: string }>({});

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErrors({});

    const parsed = requestLinkSchema.safeParse({ email, ageConsent });
    if (!parsed.success) {
      const fe = parsed.error.flatten().fieldErrors;
      setErrors({
        email: fe.email ? t("login.errorEmail") : undefined,
        ageConsent: fe.ageConsent ? t("login.errorAgeConsent") : undefined,
      });
      return;
    }

    setSubmitting(true);
    try {
      await api<void>("/api/auth/request-link", { method: "POST", body: parsed.data });
      navigate("/login/check-email", { state: { email: parsed.data.email } });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t("login.errorGeneric");
      setErrors({ form: msg });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="container auth">
      <div className="surface auth__card">
        <h1>{t("login.title")}</h1>
        <p>{t("login.intro")}</p>

        <form onSubmit={onSubmit} noValidate aria-describedby={errors.form ? "form-error" : undefined}>
          <div className="field">
            <label htmlFor="email">{t("login.emailLabel")}</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? "email-error" : undefined}
              disabled={submitting}
            />
            {errors.email && (
              <p id="email-error" className="field__error" role="alert">{errors.email}</p>
            )}
          </div>

          <div className="field field--check">
            <label htmlFor="age-consent">
              <input
                id="age-consent"
                type="checkbox"
                checked={ageConsent}
                onChange={(e) => setAgeConsent(e.target.checked)}
                aria-invalid={Boolean(errors.ageConsent)}
                aria-describedby={errors.ageConsent ? "age-error" : "age-help"}
                disabled={submitting}
              />
              <span>
                <Trans i18nKey="login.ageConsentLabel" />
              </span>
            </label>
            <p id="age-help" className="field__help">{t("login.ageHelp")}</p>
            {errors.ageConsent && (
              <p id="age-error" className="field__error" role="alert">{errors.ageConsent}</p>
            )}
          </div>

          {errors.form && (
            <p id="form-error" className="field__error" role="alert">{errors.form}</p>
          )}

          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? t("login.submitting") : t("login.submit")}
          </button>
        </form>

        <p className="auth__legal">
          <Trans
            i18nKey="login.legal"
            components={{ 1: <Link to="/privacidade" /> }}
          />
        </p>
      </div>
    </div>
  );
}
